import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ProductDetail as ProductDetailModel } from '@/api/models';
import { ProductDetailView } from './ProductDetailView';
import { ProductNotFound } from './ProductNotFound';
import { useProductDetail, type ProductDetailBranch } from './useProductDetail';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';
import type { DraftPublisher } from './draftPanel/useDraftPublish';
import { getNodeTestIdProp } from '@/utils';

type ProductDetailContentProps = {
    data: ProductDetailModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
    isStockLoading: boolean;
    expandedVariantIds: Set<string>;
    onToggleVariant: (variantId: string) => void;
    onEdit: () => void;
    branch: ProductDetailBranch;
    barcodes: VariantBarcodesEditor;
    draft: DraftPublisher;
};

/**
 * /products/:productId — the full product graph (header, options, variants with
 * batches). Self-served via `useProductDetail`; the throw lives in the inner
 * Content child so the `ErrorBoundary` (a load failure shows the retry
 * fallback) can catch it. A product that no longer exists is not thrown: the
 * page says so, with no Try again (bos-app#136).
 *
 * While the product loads the View is already on screen, with Edit and the
 * branch field live and placeholders for the product's own content
 * (bos-app#139). The not-found state still replaces the whole page.
 */
export const ProductDetail: React.FC = () => {
    const {
        status,
        data,
        error,
        isLoading,
        isStockLoading,
        expandedVariantIds,
        onToggleVariant,
        onRetry,
        onEdit,
        branch,
        barcodes,
        draft,
    } = useProductDetail();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('products.detail.error.title'),
            errorMessage: t('products.detail.error.message'),
            errorRetry: t('products.detail.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-8"
            {...getNodeTestIdProp('product-detail-page')}
            id="product-detail-page"
        >
            {status === 'notFound' ? (
                <ProductNotFound />
            ) : (
                <ErrorBoundary
                    data-testid="product-detail-error-boundary"
                    fallbackMessage={copy.errorMessage}
                    id="product-detail-error-boundary"
                    onRetry={onRetry}
                    resetKeys={[error]}
                    retryLabel={copy.errorRetry}
                    title={copy.errorTitle}
                >
                    <ProductDetailContent
                        barcodes={barcodes}
                        branch={branch}
                        data={data}
                        draft={draft}
                        error={error}
                        expandedVariantIds={expandedVariantIds}
                        isLoading={isLoading}
                        isStockLoading={isStockLoading}
                        onEdit={onEdit}
                        onToggleVariant={onToggleVariant}
                    />
                </ErrorBoundary>
            )}
        </div>
    );
};

const ProductDetailContent: React.FC<ProductDetailContentProps> = ({
    data,
    error,
    isLoading,
    isStockLoading,
    expandedVariantIds,
    onToggleVariant,
    onEdit,
    branch,
    barcodes,
    draft,
}) => {
    if (error) {
        throw error;
    }

    return (
        <ProductDetailView
            barcodes={barcodes}
            branch={branch}
            data={isLoading ? null : data}
            draft={draft}
            expandedVariantIds={expandedVariantIds}
            isStockLoading={isStockLoading}
            onEdit={onEdit}
            onToggleVariant={onToggleVariant}
        />
    );
};
